import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  Business,
  CalendarToday,
  Assignment,
  DirectionsCar,
  CurrencyRupee,
} from "@mui/icons-material";
import { ResponsivePie } from "@nivo/pie";
import { driverRequestsApis } from "../../Data/axios";
import config from "../../Data/AuthUser";
import StatusAlert from "../../StatusAlertList/StatusAlert";

function ViewMoreDriverRequest() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");

  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch (parseInt(status)) {
      case 0:
        return { color: "#f44336", bg: "#ffebee" }; // Red for Canceled
      case 1:
        return { color: "#ff9800", bg: "#fff3e0" }; // Orange for Pending
      case 2:
        return { color: "#4caf50", bg: "#e8f5e9" }; // Green for Accepted
      case 3:
        return { color: "#2196f3", bg: "#e3f2fd" }; // Blue for In Progress
      case 4:
        return { color: "#9c27b0", bg: "#f3e5f5" }; // Purple for Partial
      case 5:
        return { color: "#00bcd4", bg: "#e0f2f1" }; // Teal for Completed
      default:
        return { color: "#757575", bg: "#f5f5f5" }; // Gray for default
    }
  };

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      const response = await driverRequestsApis.getById(id, 'admin');
      
      if (response.success) {
        setRequestData(response.data);
      } else {
        showAlert(response.message || "Failed to fetch request details", "error");
      }
    } catch (error) {
      console.error("Error fetching request details:", error);
      showAlert("An error occurred while fetching request details", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchRequestDetails();
    }
  }, [id]);

  const handleBack = () => {
    navigate("/driver_requests");
  };

  if (loading) {
    return (
      <Box sx={{ padding: 3, backgroundColor: '#ffffff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width={200} height={40} sx={{ ml: 2 }} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (!requestData) {
    return (
      <Box sx={{ padding: 3, textAlign: 'center', backgroundColor: '#ffffff' }}>
        <Typography variant="h6" color="error">
          Request not found
        </Typography>
        <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const statusStyle = getStatusColor(requestData.status);

  // Calculate data for pie chart
  const totalRequirements = requestData.items?.reduce((sum, item) => sum + item.no_of_drivers, 0) || 0;
  const filledPositions = requestData.items?.reduce((sum, item) => sum + item.no_filled, 0) || 0;
  const remainingPositions = totalRequirements - filledPositions;

  const pieChartData = [
    {
      id: "filled",
      label: "Filled Positions",
      value: filledPositions,
      color: "#4caf50"
    },
    {
      id: "remaining",
      label: "Remaining Positions", 
      value: remainingPositions,
      color: "#ff9800"
    }
  ].filter(item => item.value > 0); // Only show segments with values greater than 0

  return (
    <>
      <StatusAlert
        open={alertOpen}
        setOpen={setAlertOpen}
        message={alertMessage}
        severity={alertSeverity}
      />
      
      <Box sx={{ padding: 3, backgroundColor: '#ffffff', minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2, color: '#333' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" fontWeight="bold" color="#333">
            Driver Request Details
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Main Details Card */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3, backgroundColor: '#ffffff', color: '#333' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    src={
                      requestData.app_user?.image && requestData.app_user.image !== "non"
                        ? `${config.apiImageUrl}${requestData.app_user.image}`
                        : undefined
                    }
                    sx={{ width: 80, height: 80, mr: 3 }}
                  >
                    <Person fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="#333">
                      {requestData.app_user?.username || "N/A"}
                    </Typography>
                    <Typography variant="body1" color="#666">
                      Client ID: {requestData.client_id}
                    </Typography>
                    <Chip
                      label={requestData.status_text}
                      sx={{
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.color,
                        fontWeight: 'bold',
                        mt: 1
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Request Information */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Assignment sx={{ mr: 1, color: '#1976d2' }} />
                      <Box>
                        <Typography variant="body2" color="#666">
                          Total Drivers Requested
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="#333">
                          {requestData.no_of_users}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Person sx={{ mr: 1, color: '#1976d2' }} />
                      <Box>
                        <Typography variant="body2" color="#666">
                          Assigned To
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="#333">
                          {requestData.assigned_to?.name || "Not Assigned"}
                        </Typography>
                        {requestData.assigned_to?.role && (
                          <Typography variant="body2" color="#666">
                            {requestData.assigned_to.role}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarToday sx={{ mr: 1, color: '#1976d2' }} />
                      <Box>
                        <Typography variant="body2" color="#666">
                          Created Date
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="#333">
                          {formatDate(requestData.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarToday sx={{ mr: 1, color: '#1976d2' }} />
                      <Box>
                        <Typography variant="body2" color="#666">
                          Last Updated
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="#333">
                          {formatDate(requestData.updated_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* Note Section */}
                {requestData.note && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="h6" fontWeight="bold" mb={1} color="#333">
                        Note
                      </Typography>
                      <Typography variant="body1" color="#333" sx={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: 2, 
                        borderRadius: 1,
                        border: '1px solid #e9ecef'
                      }}>
                        {requestData.note}
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Vehicle Requirements Table */}
            <Card sx={{ backgroundColor: '#ffffff', color: '#333' }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2} color="#333">
                  Vehicle Requirements
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: '#ffffff' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ color: '#333' }}><strong>Vehicle</strong></TableCell>
                        <TableCell sx={{ color: '#333' }}><strong>Description</strong></TableCell>
                        <TableCell align="center" sx={{ color: '#333' }}><strong>Drivers Needed</strong></TableCell>
                        <TableCell align="center" sx={{ color: '#333' }}><strong>Filled</strong></TableCell>
                        <TableCell sx={{ color: '#333' }}><strong>Salary</strong></TableCell>
                        <TableCell sx={{ color: '#333' }}><strong>Brand/Model</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requestData.items?.map((item, index) => (
                        <TableRow key={item.id} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                          <TableCell sx={{ color: '#333' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {item.vehicle?.model?.engine ? (
                                <img
                                  src={`${config.apiImageUrl}${item.vehicle.model.engine}`}
                                  alt="Vehicle Engine"
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    marginRight: '8px',
                                    objectFit: 'contain'
                                  }}
                                />
                              ) : (
                                <DirectionsCar sx={{ mr: 1, color: '#1976d2' }} />
                              )}
                              <Typography variant="body2" fontWeight="bold" color="#333">
                                {item.vehicle_id}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#333' }}>
                            <Typography variant="body2" color="#333">
                              {item.description || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#333' }}>
                            <Chip 
                              label={item.no_of_drivers} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#333' }}>
                            <Chip 
                              label={item.no_filled} 
                              size="small" 
                              color={item.no_filled > 0 ? "success" : "default"}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#333' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CurrencyRupee sx={{ fontSize: 16, mr: 0.5, color: '#4caf50' }} />
                              <Typography variant="body1" fontWeight="bold" color="#333">
                                {item.salary}
                              </Typography>
                              <Typography variant="caption" color="#666" sx={{ ml: 1 }}>
                                /{item.salary_type_text}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#333' }}>
                            <Box>
                              <Typography variant="body2" fontWeight="bold" color="#333">
                                {item.vehicle?.brand?.brand || "N/A"}
                              </Typography>
                              <Typography variant="caption" color="#666">
                                {item.vehicle?.model?.model || "N/A"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Summary Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ backgroundColor: '#ffffff', color: '#333' }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2} color="#333">
                  Request Summary
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="#666">
                    Request ID
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="#333">
                    #{requestData.id}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="#666">
                    Total Requirements
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {requestData.items?.reduce((sum, item) => sum + item.no_of_drivers, 0) || 0}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="#666">
                    Filled Positions
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {requestData.items?.reduce((sum, item) => sum + item.no_filled, 0) || 0}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="#666">
                    Remaining Positions
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {requestData.items?.reduce((sum, item) => sum + (item.no_of_drivers - item.no_filled), 0) || 0}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="#666">
                    Created By
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="#333">
                    {requestData.created_by}
                  </Typography>
                </Box>

                {/* <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleBack}
                    fullWidth
                  >
                    Back to Requests
                  </Button>
                </Box> */}
              </CardContent>
            </Card>

            {/* Pie Chart Card */}
            <Card sx={{ backgroundColor: '#ffffff', color: '#333', mt: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2} color="#333">
                  Requirements Overview
                </Typography>
                {totalRequirements > 0 ? (
                  <Box sx={{ height: 300 }}>
                    <ResponsivePie
                      data={pieChartData}
                      margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                      innerRadius={0.5}
                      padAngle={0.7}
                      cornerRadius={3}
                      activeOuterRadiusOffset={8}
                      borderWidth={1}
                      borderColor={{
                        from: 'color',
                        modifiers: [['darker', 0.2]]
                      }}
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsTextColor="#333333"
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor={{
                        from: 'color',
                        modifiers: [['darker', 2]]
                      }}
                      colors={{ datum: 'data.color' }}
                      theme={{
                        tooltip: {
                          container: {
                            background: '#ffffff',
                            color: '#333333',
                            fontSize: '14px',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }
                        },
                        labels: {
                          text: {
                            fontSize: 12,
                            fontWeight: 'bold'
                          }
                        }
                      }}
                      motionConfig="gentle"
                      transitionMode="innerRadius"
                      legends={[
                        {
                          anchor: 'bottom',
                          direction: 'row',
                          justify: false,
                          translateX: 0,
                          translateY: 56,
                          itemsSpacing: 0,
                          itemWidth: 100,
                          itemHeight: 18,
                          itemTextColor: '#333',
                          itemDirection: 'left-to-right',
                          itemOpacity: 1,
                          symbolSize: 18,
                          symbolShape: 'circle',
                          effects: [
                            {
                              on: 'hover',
                              style: {
                                itemTextColor: '#000'
                              }
                            }
                          ]
                        }
                      ]}
                    />
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="#666">
                      No data available for chart
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default ViewMoreDriverRequest;
